import React from 'react';

import { useRouter } from 'next/router';

import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
} from '@mui/material';
import classNames from 'classnames';

import { ChangeEventValue } from 'google-map-react';

import { COUNTRY_NAME_EN } from '@kamernet/constants/Common';
import {
  MAP_INITIAL_ZOOM_SIZE,
  MAP_MARGIN,
  HOVER_DISTANCE,
  MAP_INITIAL_LATITUDE,
  MAP_INITIAL_LONGITUDE,
} from '@kamernet/constants/Listings';
import { useIntl } from '@kamernet/core/Intl';
import { CommonStyles } from '@kamernet/styles/CommonStyles';
import { useMediaIsDesktop } from '@kamernet/utilities/Media/useMediaIsDesktop';

import { KnGoogleMap } from '@kamernet/modules/GoogleMap/GoogleMap';
import { ListingsContext } from '@kamernet/modules/Listing/context/ListingsContext';
import { useListingsPageUrl } from '@kamernet/modules/Listing/hooks/useListingsPageUrl';
import { useMapListingDispatcher } from '@kamernet/modules/Listing/hooks/useMapListingDispatcher';
import { SearchView } from '@kamernet/modules/Listing/types';

import { useMapClusters } from '../../../../hooks/useMapCluster';

import { areCoordsEqual } from './areCoordsEqual';
import { RoomMarker, ClusterMarker } from './Markers';
import styles from './SearchMap.module.css';
import { MarkerData, MapBounds } from './types';
import { isMapInInitialState } from './Utils';

const intialCoordinates = {
  lat: MAP_INITIAL_LATITUDE,
  lng: MAP_INITIAL_LONGITUDE,
};

type Props = {
  openMobileGrid: () => void;
};

export const SearchMap = ({ openMobileGrid }: Props) => {
  const mapRef = React.useRef<any>();
  const isDesktop = useMediaIsDesktop();
  const { listingsState } = ListingsContext.useContext();
  const { updateUrl } = useListingsPageUrl();
  const [zoom, setZoom] = React.useState(MAP_INITIAL_ZOOM_SIZE);
  const [bounds, setBounds] = React.useState<MapBounds>();
  const [searchOnMapMove, setSearchOnMapMove] =
    React.useState<boolean>(isDesktop);
  const [center, setCenter] = React.useState(intialCoordinates);
  const [showResetButton, setShowResetButton] = React.useState<boolean>(false);
  const initialized = React.useRef(false);
  const { T } = useIntl();
  const [selectedMarker, setSelectedMarker] = React.useState<MarkerData>();
  const router = useRouter();
  const searchParams = router.query;
  React.useEffect(() => {
    if (mapRef.current) {
      setZoom(MAP_INITIAL_ZOOM_SIZE);
      setCenter(intialCoordinates);
    }
  }, [listingsState.filterResult.filters.location]);
  const { clusters, getClusterZoom } = useMapClusters(
    bounds,
    zoom,
    searchOnMapMove,
  );
  const { mapListingStateDispatcher } = useMapListingDispatcher();
  const hasCoords = Boolean(
    searchParams.nwlng &&
      searchParams.selng &&
      searchParams.nwlat &&
      searchParams.selat,
  );
  const handleGoogleApiLoaded = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async ({ map, maps }: any) => {
      mapRef.current = map;
      if (!initialized.current && hasCoords) {
        initialized.current = true;
        const boundsObj = new maps.LatLngBounds();
        const swLatLng = new maps.LatLng(
          searchParams.selat,
          searchParams.selng,
        );
        boundsObj.extend(swLatLng);
        const neLatLng = new maps.LatLng(
          searchParams.nwlat,
          searchParams.nwlng,
        );
        boundsObj.extend(neLatLng);

        map.fitBounds(boundsObj);
      } else {
        const address = `${listingsState.filterResult.filters.location?.cityName}, ${COUNTRY_NAME_EN}`;
        const geocoder = new maps.Geocoder();
        geocoder.geocode({ address }, (results: any, status: string) => {
          if (status === 'OK') {
            map.fitBounds(results[0].geometry.bounds);
            setCenter(results[0].geometry.location);
          }
        });
      }

      initialized.current = true;
    },
    [
      listingsState.filterResult.filters.location?.cityName,
      hasCoords,
      searchParams.selat,
      searchParams.selng,
      searchParams.nwlat,
      searchParams.nwlng,
    ],
  );

  const handleMapBoundsChange = React.useCallback(
    async (mapChange: ChangeEventValue) => {
      if (!initialized.current && hasCoords) {
        return;
      }

      updateUrl({
        ...listingsState.filterResult.filters,
        lat_hi: mapChange.bounds.nw.lat,
        lng_lo: mapChange.bounds.nw.lng,
        lat_lo: mapChange.bounds.se.lat,
        lng_hi: mapChange.bounds.se.lng,
        searchview: SearchView.Map,
      });

      if (
        !bounds ||
        !areCoordsEqual(mapChange.bounds.nw.lng, bounds.nw.lng) ||
        !areCoordsEqual(mapChange.bounds.se.lng, bounds.se.lng) ||
        !areCoordsEqual(mapChange.bounds.nw.lat, bounds.nw.lat) ||
        !areCoordsEqual(mapChange.bounds.se.lat, bounds.se.lat)
      ) {
        const mappedBounds: MapBounds = {
          nw: {
            lat: mapChange.bounds.nw.lat,
            lng: mapChange.bounds.nw.lng,
          },
          se: {
            lat: mapChange.bounds.se.lat,
            lng: mapChange.bounds.se.lng,
          },
        };
        setZoom(mapChange.zoom);
        setBounds(mappedBounds);
        setCenter({ lat: mapChange.center.lat, lng: mapChange.center.lng });
        setShowResetButton(
          isMapInInitialState(mapChange.center.lat, mapChange.center.lng, zoom),
        );
      }
    },
    [bounds, hasCoords, updateUrl, zoom, listingsState.filterResult.filters],
  );

  const handleMarkerClick = React.useCallback(
    async (latitude: number, longitude: number, count: number) => {
      if (!isDesktop) {
        openMobileGrid();
      }
      const markerData = {
        latitude,
        Longitude: longitude,
        count,
      };
      setSelectedMarker(markerData);
      mapListingStateDispatcher([markerData]);
    },
    [isDesktop, mapListingStateDispatcher, openMobileGrid],
  );
  const handleResetClick = React.useCallback(() => {
    if (mapRef.current) {
      setZoom(MAP_INITIAL_ZOOM_SIZE);
      setCenter(intialCoordinates);
    }
  }, []);
  const onSearchOnMoveToggle = React.useCallback(() => {
    setSearchOnMapMove(!searchOnMapMove);
  }, [searchOnMapMove]);
  const handleClusterClick = React.useCallback(
    (id: number, coords: number[]) => {
      window.scrollTo(0, 200);
      let [lng, lat] = coords;
      lat = lat || 0;
      lng = lng || 0;
      const newZoom = getClusterZoom(id) || zoom + 1;
      setCenter({ lat, lng });
      setZoom(newZoom);
    },
    [zoom, getClusterZoom],
  );
  return (
    <Box className={styles.root}>
      <KnGoogleMap
        center={center}
        zoom={zoom}
        options={{
          fullscreenControl: false,
          gestureHandling: !isDesktop ? 'cooperative' : 'greedy',
          zoomControl: isDesktop,
        }}
        margin={MAP_MARGIN}
        hoverDistance={HOVER_DISTANCE}
        resetBoundsOnResize
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={handleGoogleApiLoaded}
        onChange={handleMapBoundsChange}
      >
        {clusters.map(cluster => {
          const [longitude, latitude] = cluster.geometry.coordinates;
          return cluster.properties.cluster ? (
            <ClusterMarker
              key={`cluster-${cluster.id}`}
              id={parseInt(cluster.id ? cluster.id.toString() : '0', 10)}
              lat={latitude || 0}
              lng={longitude || 0}
              rooms={cluster.properties.rooms}
              onPinClick={handleClusterClick}
            />
          ) : (
            <RoomMarker
              key={`cluster-${cluster.id}`}
              id={parseInt(cluster.id ? cluster.id.toString() : '0', 10)}
              lat={latitude || 0}
              lng={longitude || 0}
              rooms={cluster.properties.rooms}
              isSelected={
                selectedMarker
                  ? latitude === selectedMarker.latitude &&
                    longitude === selectedMarker.Longitude
                  : false
              }
              handleMarkerClick={() =>
                handleMarkerClick(
                  latitude || 0,
                  longitude || 0,
                  cluster.properties.rooms.length,
                )
              }
            />
          );
        })}
      </KnGoogleMap>

      <div className={styles.container}>
        {!showResetButton && (
          <div
            className={classNames(
              styles.resetControl,
              CommonStyles.displayFlex,
            )}
          >
            <Button
              startIcon={<RestartAltIcon />}
              variant="contained"
              size="small"
              color="tertiary"
              onClick={handleResetClick}
            >
              <Typography variant="body3">
                {T('search_page.map.reset_map')}
              </Typography>
            </Button>
          </div>
        )}
        {isDesktop && (
          <div className={styles.mapControlSearchOption}>
            <FormControlLabel
              control={
                <Checkbox
                  className={CommonStyles.padding_left_2}
                  checked={searchOnMapMove}
                  onChange={onSearchOnMoveToggle}
                />
              }
              label={
                <Typography variant="body3">
                  {T('map_view.search_as_map_moves')}
                </Typography>
              }
              onClick={handleResetClick}
            />
          </div>
        )}
      </div>
    </Box>
  );
};
